import { AlertCircle, FileText } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import {
  useCreateEncounter,
  useGetEncounterById,
  useGetPatientById,
  useListEncountersByPatient,
  useListMedicalCodesByEncounter,
} from "@elation/api-client/src/stitch-web";

import {
  MEDICAL_CODE_GROUPS,
  MedicalCodeEntity,
} from "@src/features/medical-codes";
import {
  Flexbox,
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@src/shared/components/ui/alert";
import { Card, CardContent } from "@src/shared/components/ui/card";
import { Skeleton } from "@src/shared/components/ui/skeleton";
import usePrevious from "@src/shared/hooks/usePrevious";
import { navigationV2 } from "@src/shared/navigation/v2";
import { getServerErrorMessage } from "@src/shared/utils/http/errors";
import { captureException } from "@src/shared/utils/observability/sentry";

import {
  PatientDetailHeaderV2,
  type PatientLatestVisitMedicalCodeV2,
  PatientLatestVisitV2,
  PatientPreviousVisitsV2,
} from "../../../components/v2";

const PATIENT_ENCOUNTERS_LIMIT = 8;

export interface PatientDetailPageV2Props {
  patientId: number | null;
}

function PatientDetailSkeleton() {
  return (
    <VerticalFlexbox
      className="mx-auto min-h-full w-full max-w-[860px] bg-background px-[18px] py-4"
      aria-busy="true"
    >
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="space-between"
        className="gap-4 rounded-card border border-border bg-surface p-card shadow-soft"
      >
        <HorizontalFlexbox alignItems="center" className="min-w-0 gap-3">
          <Skeleton className="size-12 rounded-full" />
          <VerticalFlexbox className="gap-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-3 w-56" />
          </VerticalFlexbox>
        </HorizontalFlexbox>
        <Skeleton className="h-9 w-32 rounded-[9px]" />
      </HorizontalFlexbox>

      <VerticalFlexbox className="mt-4 gap-4">
        <Skeleton className="h-48 rounded-card" />
        <Skeleton className="h-40 rounded-card" />
      </VerticalFlexbox>
    </VerticalFlexbox>
  );
}

function PatientDetailError({ message }: { message?: string | null }) {
  return (
    <VerticalFlexbox className="mx-auto min-h-full w-full max-w-[860px] bg-background px-[18px] py-4">
      <Alert
        variant="destructive"
        className="rounded-card border-destructive/40 bg-destructive/5 p-card"
      >
        <AlertCircle className="size-4" aria-hidden="true" />
        <AlertTitle>Unable to load patient</AlertTitle>
        <AlertDescription>
          {message ?? "Something went wrong retrieving this patient."}
        </AlertDescription>
      </Alert>
    </VerticalFlexbox>
  );
}

function EmptyPastVisit() {
  return (
    <Card className="rounded-card border-dashed bg-surface shadow-none">
      <CardContent className="px-6 py-10 text-center">
        <Flexbox
          alignItems="center"
          justifyContent="center"
          className="mx-auto mb-4 size-11 rounded-[13px] bg-primary/5 text-primary"
        >
          <FileText className="size-5" aria-hidden="true" />
        </Flexbox>
        <h2 className="text-[19px] font-semibold tracking-[-0.02em] text-surface-foreground">
          No past visits yet
        </h2>
        <p className="mx-auto mt-2 max-w-[420px] text-sm leading-6 text-muted-foreground">
          Start a visit for this patient to prepare the agenda, generate the
          note, and surface billing codes.
        </p>
      </CardContent>
    </Card>
  );
}

function VisitHistorySkeleton() {
  return (
    <VerticalFlexbox className="gap-4" aria-busy="true">
      <Skeleton className="h-64 rounded-card" />
      <Skeleton className="h-36 rounded-card" />
    </VerticalFlexbox>
  );
}

function VisitHistoryError({ message }: { message?: string | null }) {
  return (
    <Alert
      variant="destructive"
      className="rounded-card border-destructive/40 bg-destructive/5 p-card"
    >
      <AlertCircle className="size-4" aria-hidden="true" />
      <AlertTitle>Unable to load visits</AlertTitle>
      <AlertDescription>
        {message ?? "Something went wrong retrieving this patient's visits."}
      </AlertDescription>
    </Alert>
  );
}

export function PatientDetailPageV2({ patientId }: PatientDetailPageV2Props) {
  const navigate = useNavigate();
  const [startVisitError, setStartVisitError] = useState<string | null>(null);
  const previousPatientId = usePrevious(patientId);
  const isInvalidPatientId = patientId == null;

  if (previousPatientId !== undefined && previousPatientId !== patientId) {
    setStartVisitError(null);
  }

  const patientQuery = useGetPatientById(patientId ?? 0, {
    query: { enabled: !isInvalidPatientId, retry: false },
  });
  const encountersQuery = useListEncountersByPatient(
    patientId ?? 0,
    {
      limit: PATIENT_ENCOUNTERS_LIMIT,
      sortBy: "startedAt",
      order: "desc",
    },
    {
      query: { enabled: !isInvalidPatientId, retry: false },
    },
  );
  const latestEncounter = encountersQuery.data?.data.items[0] ?? null;
  const previousEncounters = encountersQuery.data?.data.items.slice(1) ?? [];
  const encounterQuery = useGetEncounterById(latestEncounter?.id ?? 0, {
    query: { enabled: latestEncounter?.id != null },
  });
  const medicalCodesQuery = useListMedicalCodesByEncounter(
    latestEncounter?.id ?? 0,
    {
      query: {
        enabled: latestEncounter?.id != null,
        retry: false,
        refetchInterval: (query) => {
          return MedicalCodeEntity.medicalCodesPollIntervalMs(
            query.state.data?.data,
          );
        },
      },
    },
  );
  const createEncounter = useCreateEncounter();
  const isCodingPending = MedicalCodeEntity.isCodingPending(
    medicalCodesQuery.data?.data,
  );
  const medicalCodes = useMemo<PatientLatestVisitMedicalCodeV2[]>(() => {
    const data = medicalCodesQuery.data?.data;
    if (!data) return [];

    const groups = MedicalCodeEntity.groupRows(data);

    return MEDICAL_CODE_GROUPS.flatMap((group) =>
      groups[group].map((row) => ({
        id: `${group}-${row.code}`,
        code: MedicalCodeEntity.codeLabel(row) ?? row.code,
        description: row.description,
        groupLabel: MedicalCodeEntity.groupLabel(group),
      })),
    );
  }, [medicalCodesQuery.data?.data]);

  const startVisit = async () => {
    if (patientId == null) {
      setStartVisitError("Patient not found.");
      return;
    }

    setStartVisitError(null);

    try {
      const result = await createEncounter.mutateAsync({
        data: { patientId },
      });

      navigate(navigationV2.visit(result.data.id), {
        state: { source: "v2 patient detail", patientId },
      });
    } catch (error) {
      captureException(error, {
        tags: { feature: "patients-v2", action: "start-visit" },
        extra: { patientId },
      });

      const message =
        getServerErrorMessage(error) ??
        "Unable to start the visit. Please try again.";
      setStartVisitError(message);
      toast.error(message);
    }
  };

  if (!isInvalidPatientId && patientQuery.isLoading) {
    return <PatientDetailSkeleton />;
  }

  if (isInvalidPatientId || patientQuery.isError) {
    return (
      <PatientDetailError
        message={
          isInvalidPatientId
            ? "The patient link is invalid."
            : (getServerErrorMessage(patientQuery.error) ?? null)
        }
      />
    );
  }

  return (
    <VerticalFlexbox className="mx-auto min-h-full w-full max-w-[860px] bg-background px-[18px] py-4">
      <PatientDetailHeaderV2
        patient={patientQuery.data?.data}
        isStartingVisit={createEncounter.isPending}
        onStartVisit={() => void startVisit()}
      />

      {startVisitError ? (
        <Alert
          variant="destructive"
          className="mt-4 rounded-card border-destructive/40 bg-destructive/5"
        >
          <AlertCircle className="size-4" aria-hidden="true" />
          <AlertTitle>Unable to start visit</AlertTitle>
          <AlertDescription>{startVisitError}</AlertDescription>
        </Alert>
      ) : null}

      <main className="mt-4">
        {encountersQuery.isError ? (
          <VisitHistoryError
            message={getServerErrorMessage(encountersQuery.error) ?? null}
          />
        ) : encountersQuery.isLoading ? (
          <VisitHistorySkeleton />
        ) : latestEncounter ? (
          <VerticalFlexbox className="gap-5">
            <PatientLatestVisitV2
              latestEncounter={latestEncounter}
              noteText={encounterQuery.data?.data.note}
              medicalCodes={medicalCodes}
              isNoteLoading={encounterQuery.isLoading}
              isNoteError={encounterQuery.isError}
              noteErrorMessage={
                getServerErrorMessage(encounterQuery.error) ?? null
              }
              isLoadingCodes={
                medicalCodesQuery.isLoading ||
                medicalCodesQuery.isFetching ||
                isCodingPending
              }
              isCodesError={medicalCodesQuery.isError}
            />
            <PatientPreviousVisitsV2
              encounters={previousEncounters}
              onEncounterSelect={(encounterId) => {
                navigate(navigationV2.visit(encounterId), {
                  state: { source: "v2 patient detail history", patientId },
                });
              }}
            />
          </VerticalFlexbox>
        ) : (
          <EmptyPastVisit />
        )}
      </main>
    </VerticalFlexbox>
  );
}
